/* cs-card-added (5 October 2026): the "added" state for every product card
   with an Add button. Find your layers, Build your setup (homepage and
   /pages/build-your-setup), the collection cards, the product page pairs,
   and the cart's suggestion rows all hand their adds to this one file.

   After a successful /cart/add.js:
     - a gold ring ripples out round the card once
     - the card keeps a gold "in" state, and an "In your bag" badge sits on
       the top right of its photo
     - the header bag count redraws and bumps
     - the Add button is swapped for a navy split pill: "Added" and "Remove"
     - a toast at the bottom: "Added · name", Undo, View bag. 4s, and a new
       add replaces it
   Nothing opens the drawer except View bag. The drawer's contents are still
   redrawn behind the scenes, so it is right when it is opened and the gift
   ladder (assets/cs-cart-drawer.js) settles as usual.

   Remove posts /cart/change.js with quantity 0 for that card's line key.
   Undo puts the line back to what it was before the add.

   The drawer's line Remove link, and its minus at a quantity of 1, take
   the whole line out through removeLine() (6 October 2026). The same toast
   then reads "Removed · name" without View bag, since the bag is open, and
   Undo adds the line back at the quantity it had.

   On load the cart is read once, and any card whose variant is already in
   it shows the "in" state, without the ripple. Any later change to the
   drawer (a quantity, a gift) re-reads the cart and repaints the cards.

   Markup contract:
     [data-csa-card]    the card; carries data-product-id and, when one
                        variant is meant, data-variant (the swatch cards keep
                        it current). With no data-variant, any line of that
                        product counts. Optional data-name for the toast.
     [data-csa-media]   the photo box the badge sits in
     [data-csa-add]     an Add button; data-csa-variant, or the card's
                        data-variant. Pickers call window.csAdded.add().
   A line with a price of nothing is a free gift and never marks a card.

   With reduced motion: no ripple, no bump, no badge pop, no toast slide. */

(function () {
  'use strict';
  if (window.csAdded) return;

  var TOAST_MS = 4000;
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var cart = null;
  var subs = [];
  var busy = false;
  var drawerHold = 0;

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not update your bag.');
        return json;
      });
    });
  }
  function sized(url, width) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }
  function tickSvg(size) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  }

  /* ---------- cart state ---------- */
  function paidLines(c) {
    return ((c && c.items) || []).filter(function (l) { return l.final_line_price > 0; });
  }

  function lineFor(card, c) {
    var vid = card.getAttribute('data-variant');
    var pid = card.getAttribute('data-product-id');
    var lines = paidLines(c);
    for (var i = 0; i < lines.length; i++) {
      if (vid ? String(lines[i].variant_id) === vid : String(lines[i].product_id) === pid) return lines[i];
    }
    return null;
  }

  function paintAll() {
    if (!cart) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-csa-card]'), function (card) {
      var line = lineFor(card, cart);
      if (line) setIn(card, line.key, false);
      else setOut(card);
    });
  }

  function setCart(c) {
    cart = c;
    paintAll();
    subs.forEach(function (fn) { try { fn(cart); } catch (e) { console.error(e); } });
    /* The bag bar may have just appeared under a toast already showing. */
    if (toast && !toast.hidden) toast.style.setProperty('--csa-lift', lift() + 'px');
  }

  function readCart() {
    return fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(setCart)
      .catch(function () {});
  }

  /* ---------- drawer and header, redrawn without opening ---------- */
  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function renderSections(sections) {
    if (!sections) return;
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var sec = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (sec) bubble.innerHTML = sec.innerHTML;
    }
    var html = sections['cart-drawer'];
    var drawer = document.querySelector('cart-drawer');
    if (!html || !drawer) return;
    var swap = function () {
      var doc = parse(html);
      var incoming = doc.querySelector('.drawer__inner');
      var current = drawer.querySelector('.drawer__inner');
      if (incoming && current) {
        current.className = incoming.className;
        current.innerHTML = incoming.innerHTML;
      }
      var shell = doc.querySelector('cart-drawer');
      if (shell) drawer.classList.toggle('is-empty', shell.classList.contains('is-empty'));
    };
    /* A card inside the drawer (Close the gap) shows its state for a
       moment before the redraw replaces it. */
    if (drawerHold) setTimeout(swap, drawerHold);
    else swap();
  }

  function bump() {
    if (reduced()) return;
    var count = document.querySelector('#cart-icon-bubble .cs-basket-count');
    if (!count) return;
    count.classList.remove('csa-bump');
    void count.offsetWidth;
    count.classList.add('csa-bump');
    setTimeout(function () { count.classList.remove('csa-bump'); }, 520);
  }

  function announce(source, json) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: source, cartData: json });
    }
  }

  function openBag(from) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(from);
    } else {
      window.location.href = route('cart_url', '/cart');
    }
  }

  /* ---------- card state ---------- */
  function controlOf(card) {
    return card.querySelector('[data-csa-add], [data-v2qa-open], [data-cq-add]');
  }

  function setIn(card, key, animate) {
    card.setAttribute('data-csa-key', key);
    if (card.classList.contains('csa-in')) return;
    card.classList.add('csa-in');

    var media = card.querySelector('[data-csa-media]');
    if (media && !media.querySelector('.csa-badge')) {
      var badge = document.createElement('span');
      badge.className = 'csa-badge';
      /* A hidden panel measures nothing, so its set width is read instead. */
      var w = media.getBoundingClientRect().width || parseFloat(getComputedStyle(media).width) || 999;
      if (w < 120) badge.classList.add('csa-badge--mini');
      if (animate && !reduced()) badge.classList.add('csa-badge--pop');
      badge.innerHTML = '<span class="csa-badge__tick">' + tickSvg(10) + '</span><span class="csa-badge__text">In your bag</span>';
      media.appendChild(badge);
    }

    var control = controlOf(card);
    if (control && !card.querySelector('.csa-split')) {
      var hadFocus = document.activeElement === control || control.contains(document.activeElement);
      control.classList.add('csa-hide');
      var split = document.createElement('div');
      split.className = 'csa-split';
      split.setAttribute('role', 'group');
      split.setAttribute('aria-label', 'In your bag');
      split.innerHTML =
        '<span class="csa-split__added">' + tickSvg(14) + '<span>Added</span></span>' +
        '<button type="button" class="csa-split__remove" data-csa-remove>Remove</button>';
      control.parentNode.insertBefore(split, control.nextSibling);
      if (hadFocus) split.querySelector('[data-csa-remove]').focus({ preventScroll: true });
    }
  }

  function setOut(card) {
    card.removeAttribute('data-csa-key');
    if (!card.classList.contains('csa-in')) return;
    card.classList.remove('csa-in');
    var badge = card.querySelector('.csa-badge');
    if (badge) badge.remove();
    var split = card.querySelector('.csa-split');
    var hadFocus = split && split.contains(document.activeElement);
    if (split) split.remove();
    var control = controlOf(card);
    if (control) {
      control.classList.remove('csa-hide');
      if (hadFocus) control.focus({ preventScroll: true });
    }
  }

  function ripple(card) {
    if (reduced()) return;
    var r = card.getBoundingClientRect();
    if (!r.width) return;
    var ring = document.createElement('span');
    ring.className = 'csa-ripple';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.left = r.left + window.scrollX + 'px';
    ring.style.top = r.top + window.scrollY + 'px';
    ring.style.width = r.width + 'px';
    ring.style.height = r.height + 'px';
    ring.style.borderRadius = getComputedStyle(card).borderRadius;
    document.body.appendChild(ring);
    setTimeout(function () { ring.remove(); }, 950);
  }

  /* ---------- toast ---------- */
  var toast = null;
  var toastTimer = null;

  function buildToast() {
    toast = document.createElement('div');
    toast.className = 'csa-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    toast.innerHTML =
      '<img class="csa-toast__img" alt="" width="40" height="40" data-csa-toast-img>' +
      '<p class="csa-toast__text"><span data-csa-toast-verb>Added</span> · <span data-csa-toast-name></span></p>' +
      '<button type="button" class="csa-toast__undo" data-csa-undo>Undo</button>' +
      '<button type="button" class="csa-toast__bag" data-csa-bag>View bag</button>';
    document.body.appendChild(toast);
    toast.addEventListener('mouseenter', function () { clearTimeout(toastTimer); });
    toast.addEventListener('mouseleave', armToast);
    toast.addEventListener('focusin', function () { clearTimeout(toastTimer); });
    toast.addEventListener('focusout', armToast);
  }

  function armToast() {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_MS);
  }

  function hideToast() {
    clearTimeout(toastTimer);
    if (!toast || toast.hidden) return;
    toast.classList.remove('is-in');
    setTimeout(function () { if (!toast.classList.contains('is-in')) toast.hidden = true; }, reduced() ? 0 : 260);
  }

  /* On a phone, Find your layers' bar sits fixed at the bottom: the toast
     rides above it rather than over it. */
  function lift() {
    var bar = document.querySelector('[data-fyl-bar]');
    if (!bar || bar.hidden || bar.classList.contains('is-away') || getComputedStyle(bar).position !== 'fixed') return 0;
    return Math.max(0, window.innerHeight - bar.getBoundingClientRect().top) + 8;
  }

  function showToast(info) {
    if (!toast) buildToast();
    toast._undo = info.undo;
    var img = toast.querySelector('[data-csa-toast-img]');
    if (info.image) {
      img.src = sized(info.image, 120);
      img.hidden = false;
    } else {
      img.removeAttribute('src');
      img.hidden = true;
    }
    toast.querySelector('[data-csa-toast-verb]').textContent = info.verb || 'Added';
    toast.querySelector('[data-csa-toast-name]').textContent = info.name || 'to your bag';
    toast.querySelector('[data-csa-bag]').hidden = info.bag === false;
    toast.querySelector('[data-csa-undo]').disabled = false;
    toast.style.setProperty('--csa-lift', lift() + 'px');
    toast.hidden = false;
    toast.classList.remove('is-in');
    if (reduced()) {
      toast.classList.add('is-in');
    } else {
      void toast.offsetWidth;
      toast.classList.add('is-in');
    }
    armToast();
  }

  /* ---------- add and remove ---------- */
  function add(variantId, opts) {
    opts = opts || {};
    var card = opts.card || null;
    var button = opts.button || null;
    if (!variantId || busy) return Promise.resolve(null);
    busy = true;
    if (button) button.setAttribute('aria-busy', 'true');
    if (opts.errorEl) opts.errorEl.hidden = true;

    /* What Undo goes back to: the line's quantity before this add. */
    var beforeQty = 0;
    ((cart && cart.items) || []).forEach(function (l) {
      if (String(l.variant_id) === String(variantId)) beforeQty = l.quantity;
    });

    drawerHold = card && card.closest('cart-drawer') ? (reduced() ? 400 : 900) : 0;
    return post(route('cart_add_url', '/cart/add') + '.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: SECTIONS,
      sections_url: window.location.pathname
    })
      .then(function (json) {
        var line = (json.items && json.items[0]) || null;
        renderSections(json.sections);
        drawerHold = 0;
        announce('cs-card-added', json);
        bump();
        if (card && line) {
          setIn(card, line.key, true);
          ripple(card);
        }
        /* opts.toast false: the caller shows the bag itself (the quiz opens
           the drawer), so a toast on top would say the same thing twice. */
        if (line && opts.toast !== false) {
          showToast({
            name: (card && card.getAttribute('data-name')) || opts.name || line.product_title,
            image: line.image || opts.image,
            undo: { key: line.key, quantity: beforeQty }
          });
        }
        return readCart().then(function () { return line; });
      })
      .catch(function (err) {
        drawerHold = 0;
        if (opts.errorEl) {
          opts.errorEl.textContent = err.message;
          opts.errorEl.hidden = false;
        }
        console.error('[cs-card-added]', err);
        return null;
      })
      .then(function (line) {
        busy = false;
        if (button) button.removeAttribute('aria-busy');
        return line;
      });
  }

  function change(key, quantity) {
    if (!key) return Promise.resolve(null);
    return post(route('cart_change_url', '/cart/change') + '.js', {
      id: key,
      quantity: quantity,
      sections: SECTIONS,
      sections_url: window.location.pathname
    })
      .then(function (json) {
        renderSections(json.sections);
        announce('cs-card-added', json);
        bump();
        setCart(json);
        return json;
      })
      .catch(function (err) {
        console.error('[cs-card-added]', err);
        return null;
      });
  }

  function remove(key) {
    return change(key, 0);
  }

  /* A whole line out of the drawer, whatever its quantity, with Undo. What
     Undo needs (variant, quantity, properties) is read from the cart before
     the line goes, since its key dies with it. */
  function removeLine(key, quantity) {
    if (!key || busy) return Promise.resolve(null);
    busy = true;
    var find = function () {
      return ((cart && cart.items) || []).filter(function (l) { return l.key === key; })[0] || null;
    };
    var known = find();
    return (known ? Promise.resolve() : readCart())
      .then(function () {
        var line = known || find();
        return change(key, 0).then(function (json) {
          if (json && line) {
            showToast({
              verb: 'Removed',
              name: line.product_title,
              image: line.image,
              bag: false,
              undo: { restore: { id: line.variant_id, quantity: quantity || line.quantity, properties: line.properties || {} } }
            });
          }
          return json;
        });
      })
      .then(function (json) {
        busy = false;
        return json;
      }, function () {
        busy = false;
        return null;
      });
  }

  function restore(item) {
    return post(route('cart_add_url', '/cart/add') + '.js', {
      items: [item],
      sections: SECTIONS,
      sections_url: window.location.pathname
    })
      .then(function (json) {
        renderSections(json.sections);
        announce('cs-card-added', json);
        bump();
        return readCart();
      })
      .catch(function (err) {
        console.error('[cs-card-added]', err);
      });
  }

  /* ---------- events ---------- */

  /* Drawer lines. Captured on document so Dawn's own handlers on the
     button and on cart-remove-button never see the click. Minus only counts
     when the server and the input both say 1: mid-way through a quick
     2 to 1 to 0 the server still says 2, and Dawn's path takes it. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var row = t.closest('#CartDrawer .cart-item[data-cs-key]');
    if (!row) return;
    var control = t.closest('[data-cs-remove-line]');
    if (!control) {
      control = t.closest('.quantity__button[name="minus"]');
      var input = row.querySelector('.quantity__input');
      if (!control || !input || row.getAttribute('data-cs-qty') !== '1' || parseInt(input.value, 10) !== 1) return;
    }
    if (control.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    row.classList.add('cs-removing');
    removeLine(row.getAttribute('data-cs-key'), parseInt(row.getAttribute('data-cs-qty'), 10)).then(function (json) {
      if (!json) row.classList.remove('cs-removing');
    });
  }, true);
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var addBtn = t.closest('[data-csa-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.getAttribute('aria-busy') === 'true') return;
      var card = addBtn.closest('[data-csa-card]');
      if (card && card.getAttribute('data-available') === 'false') return;
      var vid = addBtn.getAttribute('data-csa-variant') || (card && card.getAttribute('data-variant'));
      add(vid, { card: card, button: addBtn, name: addBtn.getAttribute('data-csa-name') });
      return;
    }

    var removeBtn = t.closest('[data-csa-remove]');
    if (removeBtn) {
      e.preventDefault();
      var owner = removeBtn.closest('[data-csa-card]');
      if (!owner || removeBtn.getAttribute('aria-busy') === 'true') return;
      removeBtn.setAttribute('aria-busy', 'true');
      remove(owner.getAttribute('data-csa-key')).then(function () {
        removeBtn.removeAttribute('aria-busy');
      });
      return;
    }

    if (toast && toast.contains(t)) {
      if (t.closest('[data-csa-undo]')) {
        var undo = toast._undo;
        t.closest('[data-csa-undo]').disabled = true;
        hideToast();
        if (undo && undo.restore) restore(undo.restore);
        else if (undo) change(undo.key, undo.quantity);
      } else if (t.closest('[data-csa-bag]')) {
        hideToast();
        openBag(t.closest('[data-csa-bag]'));
      }
    }
  });

  /* A swatch change on a card points it at another variant. */
  document.addEventListener('cs-vp:change', function (e) {
    var card = e.target && e.target.closest && e.target.closest('[data-csa-card]');
    if (!card || !cart) return;
    var line = lineFor(card, cart);
    if (line) setIn(card, line.key, false);
    else setOut(card);
  });

  /* Anything else that changes the cart redraws the drawer: re-read it then. */
  function watchDrawer() {
    var drawer = document.querySelector('#CartDrawer');
    if (!drawer || !window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (busy) return;
        readCart().then(refreshBubble);
      }, 300);
    }).observe(drawer, { childList: true, subtree: true });
  }

  /* The gift lines go in after the drawer redraws, so the header count is
     read again with them. */
  var lastCount = null;
  function refreshBubble() {
    if (!cart) return;
    if (lastCount !== null && lastCount === cart.item_count) return;
    lastCount = cart.item_count;
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + '?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (json) { renderSections({ 'cart-icon-bubble': json['cart-icon-bubble'] }); })
      .catch(function () {});
  }

  /* Cards the theme editor redraws, and cards drawn into the drawer, are
     painted from the cart already held. */
  document.addEventListener('shopify:section:load', paintAll);

  window.csAdded = {
    add: add,
    remove: remove,
    removeLine: removeLine,
    openBag: openBag,
    cart: function () { return cart; },
    paidLines: function () { return paidLines(cart); },
    paint: paintAll,
    subscribe: function (fn) {
      subs.push(fn);
      if (cart) fn(cart);
    }
  };

  function start() {
    readCart().then(function () { lastCount = cart ? cart.item_count : null; });
    watchDrawer();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
